import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { US_COUNTRY_ID } from '../../../../../infrastructure/context/UserContext';
import { Country } from '../../../../../shared/address/Country';
import { State } from '../../../../../shared/address/State';
import { getCountryCodeFromCountryId } from '../../../../../utilities/CountryUtility';

interface CachedPaymentInformation {
  send_check_to_name: string;
  send_check_to_phone: string;
  send_check_to_email: string;
  address_line_1: string;
  address_line_2: string;
  country_id: number;
  state: string;
  city: string;
  postal_code: string;
}

@Component({
  selector: 'app-payment-information-step',
  templateUrl: './payment-information-step.component.html',
  styleUrls: ['./payment-information-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIcon,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatRadioModule,
    MatOption,
    MatSelect,
    FormsModule,
    MatInputModule
  ]
})
export class PaymentInformationStepComponent implements OnInit {

  @Input('form') form!: FormGroup;
  @Input('countries') countries!: Country[];
  @Input('states') states!: State[];
  @Input('organization') organization!: any;
  @Input() reservation!: any;
  @Input() sendCheckToAddress!: CachedPaymentInformation;

  panelOpen: boolean = false;
  cachedPaymentInformation!: CachedPaymentInformation;

  constructor(private readonly logService: Logger) { }

  ngOnInit(): void {
    this.setupForm();
    
    if (this.sendCheckToAddress) {
      this.cachedPaymentInformation = this.sendCheckToAddress;
    }
  }

  private async setupForm() {
    this.form.get('send_check_to_address.country_id')?.valueChanges.subscribe(_ => {
      this.updatePhoneNumber(this.form.get('send_check_to_phone')?.value);
    });
  }

  onPhoneNumberChange(event: any) {
    this.updatePhoneNumber(event.target.value);
  }

  private updatePhoneNumber(unformatted: string) {
    const countryId = this.form.get('send_check_to_address.country_id')?.value || US_COUNTRY_ID;
    if (!countryId || !unformatted) {
      return;
    }

    const countryCode = getCountryCodeFromCountryId(this.countries, countryId);
    if (!countryCode) {
      this.logService.handleError(`Unable to find Country code for Country ID: ${countryId}`, `Unable to find Country code`, `Unable to find Country code for Country ID: ${countryId}`);
      return;
    }

    const ayt = new AsYouType(countryCode as CountryCode);
    this.form.patchValue({
      send_check_to_phone: ayt.input(unformatted)
    });
  }

  shouldShowStateInput(): boolean {
    return this.form.get('send_check_to_address.country_id')?.value == US_COUNTRY_ID;
  }

  handleOrganizationOrHomeownerChange(event: MatRadioChange) {
    if (event.value === 2) {
      if (this.sendCheckToAddress != undefined) {
        this.form.get('send_check_to_name')?.setValue(this.cachedPaymentInformation.send_check_to_name);
        this.form.get('send_check_to_phone')?.setValue(this.cachedPaymentInformation.send_check_to_phone);
        this.form.get('send_check_to_email')?.setValue(this.cachedPaymentInformation.send_check_to_email);
        this.form.get('send_check_to_address.address_line_1')?.setValue(this.cachedPaymentInformation.address_line_1);
        this.form.get('send_check_to_address.address_line_2')?.setValue(this.cachedPaymentInformation.address_line_2);
        this.form.get('send_check_to_address.country_id')?.setValue(this.cachedPaymentInformation.country_id);
        this.form.get('send_check_to_address.state')?.setValue(this.cachedPaymentInformation.state);
        this.form.get('send_check_to_address.city')?.setValue(this.cachedPaymentInformation.city);
        this.form.get('send_check_to_address.postal_code')?.setValue(this.cachedPaymentInformation.postal_code);
      } else {
        this.populateOrganizationPaymentInformation();
      }
    } else {
      this.form.get('send_check_to_name')?.setValue(this.reservation.property.property_owner_name);
      this.form.get('send_check_to_phone')?.setValue(this.reservation.property.property_owner_phone);
      this.form.get('send_check_to_email')?.setValue(this.reservation.property.property_owner_email);
      this.form.get('send_check_to_address.address_line_1')?.setValue(this.reservation.property.property_owner_address?.address_line_1);
      this.form.get('send_check_to_address.address_line_2')?.setValue(this.reservation.property.property_owner_address?.address_line_2);
      this.form.get('send_check_to_address.country_id')?.setValue(this.reservation.property.property_owner_address?.country_id);
      this.form.get('send_check_to_address.state')?.setValue(this.reservation.property.property_owner_address?.state);
      this.form.get('send_check_to_address.city')?.setValue(this.reservation.property.property_owner_address?.city);
      this.form.get('send_check_to_address.postal_code')?.setValue(this.reservation.property.property_owner_address?.postal_code);
    }
  }

  populateOrganizationPaymentInformation() {
    this.form.get('send_check_to_name')?.setValue(this.organization.billing_contact_name);
    this.form.get('send_check_to_phone')?.setValue(this.organization.billing_contact_phone_number);
    this.form.get('send_check_to_email')?.setValue(this.organization.billing_contact_email_address);
    this.form.get('send_check_to_address.address_line_1')?.setValue(this.organization.address.address_line_1);
    this.form.get('send_check_to_address.address_line_2')?.setValue(this.organization.address.address_line_2);
    this.form.get('send_check_to_address.country_id')?.setValue(this.organization.address.country_id);
    this.form.get('send_check_to_address.state')?.setValue(this.organization.address.state);
    this.form.get('send_check_to_address.city')?.setValue(this.organization.address.city);
    this.form.get('send_check_to_address.postal_code')?.setValue(this.organization.address.postal_code);
  }

  isHomeownerSelected(eventValue: number): boolean {
    return eventValue == 3;
  }
}